import { getConfig } from '@sortlist-frontend/config';
import { Log, Monitor } from '@sortlist-frontend/mlm';
import { isNumber, useScript } from '@sortlist-frontend/utils';
import { RefObject, useEffect, useRef, useState } from 'react';

export enum Action {
  POST_PROJECT = 'post-project',
  CREATE_PROFILE = 'create-profile',
  PROJECT_DESCRIPTION_AI = 'project-description-ai',
  VIEW_AGENCY = 'view-agency',
}

const RETRY_INTERVAL = 30_000;
const MAX_SERVER_REFUSAL_RESETS = 2;
const REFRESHABLE_TOKEN_ERRORS = ['invalid-input-response', 'timeout-or-duplicate'];

const isRefreshableTokenRefusal = (body: { errorCodes?: string[] }): boolean =>
  body.errorCodes?.some((code) => REFRESHABLE_TOKEN_ERRORS.includes(code)) ?? false;

// See documentation: https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
export function useCloudflareTurnstile({
  ref,
  action,
  disabled = false,
  onSuccess,
  onError,
  onExpired,
}: {
  ref: RefObject<HTMLDivElement> | null;
  action: Action;
  disabled?: boolean;
  onSuccess?: VoidFunction;
  onError?: (error: string) => void;
  onExpired?: VoidFunction;
}): {
  isSuccess: boolean;
  isLoading: boolean;
} {
  const { publicEnv } = getConfig();
  const [isTurnstileValidated, setIsTurnstileValidated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const serverRefusalCountRef = useRef(0);

  const loadScriptState = useScript('https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit', {
    disabled,
  });

  useEffect(() => {
    if (disabled) return;

    // If the turnstile challenge is already validated, call the success callback directly
    if (isTurnstileValidated) {
      onSuccess?.();
    }

    if (loadScriptState === 'success' && window.turnstile != null && ref?.current != null && !isTurnstileValidated) {
      const widgetId = window.turnstile.render(ref.current, {
        theme: 'light',
        callback: async (token: string) => {
          const response = await fetch(`/api/turnstile-verify?token=${token}&action=${action}`);

          const res = await response.json();

          if (response.ok) {
            setIsTurnstileValidated(true);
            onSuccess?.();
          } else {
            const canRetry =
              isRefreshableTokenRefusal(res) && serverRefusalCountRef.current < MAX_SERVER_REFUSAL_RESETS;
            if (canRetry) {
              serverRefusalCountRef.current += 1;
              window.turnstile?.reset(widgetId);
            } else {
              Monitor.captureException(`[CFTurnstile Error]: server side error`, { extra: { error: res.error } });
            }
          }

          setIsLoading(false);
        },
        action,
        appearance: 'always',
        size: 'flexible',
        sitekey: publicEnv.cloudflareTurnstileKey ?? '',
        'expired-callback': () => {
          onExpired?.();
        },
        'retry-interval': RETRY_INTERVAL,
        'error-callback': (error: string) => {
          Log.error(`[CFTurnstile Error]: error in callback`, { error });
          const errorCode = Number(error);

          // Check here: https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
          const ignoredErrorCodes = [
            106010, 106030, 106050, 100040, 100280, 110200, 110600, 200100, 300010, 300030, 300031, 600010,
          ];

          const errorCodeIsNumber = isNumber(errorCode) && !isNaN(errorCode);
          const isIgnoredErrorCode = errorCodeIsNumber && ignoredErrorCodes.includes(errorCode);

          if (!isIgnoredErrorCode) {
            Monitor.captureException(`[CFTurnstile Error]: error in callback`, {
              extra: { error },
              tags: { errorCode: errorCodeIsNumber ? error : undefined },
            });
          }

          onError?.(error);
        },
      });
    }
  }, [loadScriptState, ref?.current, isTurnstileValidated, disabled]);

  if (disabled) {
    return {
      isLoading: false,
      isSuccess: true,
    };
  }

  return {
    isLoading,
    isSuccess: isTurnstileValidated,
  };
}
